/**
 * Collaboration Provider Component
 * 
 * Manages WebSocket connection and real-time collaboration features:
 * - Comment synchronization
 * - Collaborative cursors
 * - Presence indicators
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 15.1
 */

import { useEffect, useState, useRef } from 'react';
import { WebSocketClient } from '../services/websocket-client';
import { useCommentSync } from '../hooks/useCommentSync';
import { useDAWStore } from '../store/daw-store';
import { loadCommentsFromBackend } from '../services/comments';

interface CollaborationProviderProps {
  children: React.ReactNode;
}

export function CollaborationProvider({ children }: CollaborationProviderProps) {
  const projectId = useDAWStore((state) => state.projectId);
  const currentUserId = useDAWStore((state) => state.currentUserId);
  const [wsClient, setWsClient] = useState<WebSocketClient | null>(null);
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const wsClientRef = useRef<WebSocketClient | null>(null);
  
  // Initialize WebSocket connection when project is loaded
  useEffect(() => {
    if (!projectId) {
      // No project loaded, disconnect if connected
      if (wsClientRef.current) {
        wsClientRef.current.disconnect();
        wsClientRef.current = null;
        setWsClient(null);
        setConnectionState('disconnected');
      }
      return;
    }
    
    // Create WebSocket client (Requirements 15.1)
    const client = new WebSocketClient({
      projectId,
      userId: currentUserId,
      autoReconnect: true,
      reconnectDelay: 1000,
      maxReconnectDelay: 30000,
      heartbeatInterval: 30000,
    });
    
    // Set up connection state listeners
    client.on('open', async () => {
      console.log('Collaboration: Connected to project', projectId);
      setConnectionState('connected');
      
      // Load comments from backend when connection opens (Requirements 5.4, 15.4)
      try {
        // Use lazy loading to load metadata first, text on demand (Requirement 15.4)
        const comments = await loadCommentsFromBackend(projectId, { lazyLoad: true });
        useDAWStore.setState({ comments });
        console.log(`Loaded ${comments.length} comments (metadata only, lazy loading enabled)`);
      } catch (error) {
        console.error('Failed to load comments:', error);
      }
    });
    
    client.on('close', () => {
      console.log('Collaboration: Disconnected from project');
      setConnectionState('disconnected');
    });
    
    client.on('error', (error) => {
      console.error('Collaboration: WebSocket error', error);
      setConnectionState('error');
    });
    
    client.on('reconnecting', ({ attempt, delay }) => {
      console.log(`Collaboration: Reconnecting (attempt ${attempt}, delay ${delay}ms)`);
      setConnectionState('reconnecting');
    });
    
    client.on('reconnected', () => {
      console.log('Collaboration: Reconnected to project');
      setConnectionState('connected');
    });
    
    // Connect to WebSocket server
    client.connect();
    
    // Store client reference
    wsClientRef.current = client;
    setWsClient(client);
    
    // Cleanup on unmount or project change
    return () => {
      if (wsClientRef.current) {
        wsClientRef.current.disconnect();
        wsClientRef.current = null;
      }
    };
  }, [projectId, currentUserId]);
  
  // Enable comment synchronization (Requirements 5.1, 5.2, 5.3, 5.4, 5.5)
  const { queuedOperations } = useCommentSync({
    wsClient,
    enabled: !!projectId,
  });
  
  // Display connection status in development mode
  useEffect(() => {
    if (import.meta.env.DEV && connectionState !== 'connected') {
      console.log('Collaboration status:', connectionState, 
        queuedOperations > 0 ? `(${queuedOperations} queued operations)` : '');
    }
  }, [connectionState, queuedOperations]);
  
  return <>{children}</>;
}
